import React from "react";
import { Button } from "@chakra-ui/react";

export const showInfoToast = (toast, message, position = "top") => {
  toast({
    title: "Info",
    description: message,
    status: "info",
    duration: 2000,
    isClosable: true,
    position,
  });
};

export const showErrorToast = (toast, message, position = "top") => {
  toast({
    title: "Error",
    description: message,
    status: "error",
    duration: 3000,
    isClosable: true,
    position,
  });
};

/**
 * Extract the most specific error message from an axios error, falling back
 * to the given default when the server didn't send one.
 */
export const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.error || error?.response?.data?.message || fallback;

export const showDeletedToast = (toast, position = "top") => {
  toast({
    title: "Success",
    description: "Record deleted successfully",
    status: "success",
    duration: 3000,
    isClosable: true,
    position,
  });
};

export const showWarningToast = (toast, message, position = "top") => {
  toast({
    title: "Warning",
    description: message,
    status: "warning",
    duration: 3000,
    isClosable: true,
    position,
  });
};

/**
 * Utility functions for handling common API errors
 */

/**
 * Handle API errors and display appropriate toast messages
 * @param {Error} error - The error object from the API call
 * @param {Function} toast - The toast function from Chakra UI
 * @param {Object} options - Additional options
 * @param {string} options.defaultErrorMessage - Default error message for non-409 errors
 * @param {string} options.position - Toast position (default: "top")
 * @returns {void}
 */
export const handleApiError = (error, toast, options = {}) => {
  const {
    defaultErrorMessage = "Failed to fulfill request",
    position = "top",
  } = options;

  console.error("API Error:", error);

  if (error.response && error.response.status === 409) {
    // Handle conflict errors
    toast({
      title: "Error",
      description: error.response.data.message,
      status: "warning",
      duration: 5000,
      isClosable: true,
      position,
    });
  } else {
    // Handle all other errors
    toast({
      title: "Error",
      description: defaultErrorMessage,
      status: "error",
      duration: 3000,
      isClosable: true,
      position,
    });
  }
};

/**
 * Display a success toast message
 * @param {Function} toast - The toast function from Chakra UI
 * @param {string} message - The success message to display
 * @param {string} position - Toast position (default: "top")
 * @param {Object} options - Optional configuration
 * @param {string} options.actionLabel - Label for the action button
 * @param {string} options.actionUrl - URL to navigate to when action is clicked
 * @param {Function} options.onActionClick - Custom action click handler
 * @returns {void}
 */
export const showSuccessToast = (
  toast,
  message,
  position = "top",
  options = {},
) => {
  const { actionLabel, actionUrl, onActionClick } = options;

  const handleActionClick = () => {
    if (onActionClick) {
      onActionClick();
    } else if (actionUrl) {
      // Use window.location for navigation
      window.location.href = actionUrl;
    }
  };

  // Create description with optional link
  let description = message;
  if (actionUrl || onActionClick) {
    description = (
      <>
        {message}
        <br />
        <Button
          size="sm"
          colorScheme="blue"
          variant="link"
          onClick={handleActionClick}
          textDecoration="underline"
          color="white"
          mt={1}
        >
          {actionLabel || "Redirect to View Exports"}
        </Button>
      </>
    );
  }

  const toastOptions = {
    title: "Success",
    description: description,
    status: "success",
    duration: actionUrl || onActionClick ? null : 5000, // Don't auto-hide if there's an action
    isClosable: true,
    position,
  };

  // Also try adding action property (for Chakra UI versions that support it)
  if (actionUrl || onActionClick) {
    toastOptions.action = (
      <Button
        size="sm"
        colorScheme="blue"
        variant="solid"
        onClick={handleActionClick}
        color="white"
      >
        {actionLabel || "View"}
      </Button>
    );
  }

  toast(toastOptions);
};
